import { Box, Button, Grid, Stack, Typography } from '@mui/material';
import { common } from '@mui/material/colors';
import BaseTextPasswordField from 'common/components/base-text-password-field';
import FormProvider from 'common/components/form-provider';
import React from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { createStyleSheet } from 'utils/helpers';
import { yupResolver } from '@hookform/resolvers/yup';
import { resetPasswordSchema } from 'common/schema';
import { resetPassword } from 'services/auth.service';
import { useAppDispatch } from 'common/hook';
import { setIsLoading } from 'slices/loadingSlice';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import background from 'assets/images/backgrounds/bg-repass-word.jpg';
import Logo from 'common/components/logo';
import RequiredLabel from 'common/components/required-label';

const ResetPassword: React.FC = () => {
    const { t } = useTranslation();
    const query = new URLSearchParams(useLocation().search);
    const token = query.get('token');
    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    const methods = useForm({
        resolver: yupResolver(resetPasswordSchema),
        defaultValues: {
            password: '',
            password_confirmation: '',
        },
    });
    const { handleSubmit, setError } = methods;
    const onSubmit = handleSubmit(async (data) => {
        dispatch(setIsLoading(true));
        const dataForm = {
            admin: {
                password: data.password,
                password_confirmation: data.password_confirmation,
            },
        };
        try {
            const response = await resetPassword(token as string, dataForm);
            if (response.success) {
                dispatch(setIsLoading(false));
                toast.success(response.message);
                navigate('/login');
            } else {
                if (typeof response.message === 'string') {
                    toast.error(response.message);
                }
                Object.entries(response.message).forEach(([key, value]) => {
                    setError(
                        key as keyof {
                            password: string;
                            password_confirmation: string;
                        },
                        {
                            type: 'manual',
                            message: value,
                        }
                    );
                });
                dispatch(setIsLoading(false));
            }
        } catch (error) {
            if (error instanceof Error) {
                toast.error(error.message);
            }
            dispatch(setIsLoading(false));
        }
    });

    return (
        <Box sx={styles.layout}>
            <Box
                sx={{
                    flex: 2,
                    backgroundImage: `url(${background})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'right',
                    backgroundRepeat: 'no-repeat',
                }}
            ></Box>
            <Box
                sx={{
                    flex: 1,
                    backgroundColor: common.white,
                }}
            >
                <Box padding={6}>
                    <FormProvider methods={methods} onSubmit={onSubmit}>
                        <Grid container>
                            <Grid item xs={12} md={12}>
                                <Logo width={90} height={70} />
                            </Grid>
                            <Grid item xs={12} md={12}>
                                <Typography variant="h3" sx={styles.screenName}>
                                    {t('page.reset.password.setting')}
                                </Typography>
                            </Grid>

                            <Grid item xs={12} md={12}>
                                <Stack marginBottom={'17px'} spacing={'7px'}>
                                    <RequiredLabel
                                        label={t('common.enter.new_password')}
                                        sx={{ fontSize: '14px', marginLeft: 2 }}
                                        required={false}
                                    />
                                    <BaseTextPasswordField
                                        autoComplete="off"
                                        name="password"
                                        fullWidth
                                        size="small"
                                        placeholder={t(
                                            'common.enter.new_password'
                                        )}
                                        sx={styles.styleInput}
                                    />
                                </Stack>
                            </Grid>
                            <Grid item xs={12} md={12}>
                                <Stack marginBottom={'17px'} spacing={'7px'}>
                                    <RequiredLabel
                                        label={t(
                                            'common.enter.new_password.again'
                                        )}
                                        sx={{ fontSize: '14px', marginLeft: 2 }}
                                        required={false}
                                    />
                                    <BaseTextPasswordField
                                        autoComplete="off"
                                        name="password_confirmation"
                                        fullWidth
                                        size="small"
                                        placeholder={t(
                                            'common.enter.new_password.again'
                                        )}
                                        sx={styles.styleInput}
                                    />
                                </Stack>
                            </Grid>
                            <Grid item xs={12} md={12} marginBottom={'47px'}>
                                <Typography
                                    component={'span'}
                                    sx={{
                                        fontSize: '14px',
                                    }}
                                >
                                    {t('common.password.rule')}
                                </Typography>
                            </Grid>
                            <Grid item xs={12} md={12} marginBottom={'28px'}>
                                <Button
                                    sx={styles.buttonDone}
                                    type="submit"
                                    variant="contained"
                                    color="primary"
                                    fullWidth
                                >
                                    {t('common.done')}
                                </Button>
                            </Grid>
                            <Grid item xs={12} md={12} textAlign={'center'}>
                                <Typography
                                    sx={styles.textLink}
                                    component={'a'}
                                    onClick={() => navigate('/login')}
                                >
                                    <div
                                        dangerouslySetInnerHTML={{
                                            __html: t('common.back.to.login'),
                                        }}
                                    />
                                </Typography>
                            </Grid>
                        </Grid>
                    </FormProvider>
                </Box>
            </Box>
        </Box>
    );
};

const styles = createStyleSheet({
    layout: {
        width: '100vw',
        minHeight: '100vh',
        display: 'flex',
        overflowY: 'auto',
        backgroundAttachment: 'fixed',
    },
    settingPassword: {
        width: '446px',
        padding: '25px',
        background: 'transparent',
        borderRadius: 2,
    },
    screenName: {
        fontSize: '20px',
        fontWeight: 'bold',
        lineHeight: '28px',
        marginTop: '48px',
        marginBottom: '24px',
    },
    textLink: {
        color: common.white,
        cursor: 'pointer',
        fontSize: '20px',
    },
    buttonDone: {
        lineHeight: '24.2px',
        textTransform: 'none',
        height: 42,
        borderRadius: '6px',
    },
    styleInput: {
        '& .MuiInputBase-root': {
            height: '48px',
            fontSize: '16px',
            borderRadius: '6px',
        },
    },
});

export default ResetPassword;
